import React, { useEffect, useState } from "react";
import Joyride, {
  CallBackProps,
  ACTIONS,
  STATUS,
} from "react-joyride";
import { Typography, Button, RadioButton } from "components/atoms";
import { useAppDispatch, useScreenSize } from "hooks";
import {
  useGetOnboardingTourStatusQuery,
  useSaveOnboardingTourStatusMutation,
} from "services/onBoardingTour";
import {
  useGetCourseworkQuery,
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import { useSaveThemePreferenceMutation } from "services/theme";
import { Modal } from "../Modal";
import "./index.scss";
import { decodeToken } from "utils/functions/decodeToken";
import {
  DASHBOARD_PROGRAME,
  MCAT_TEST_TYPE,
  SELF_PACED_PROGRAME,
} from "utils/constants";

interface toolTipProps {
  continuous: boolean;
  index: number;
  step: any;
  backProps: any;
  closeProps: any;
  primaryProps: any;
  tooltipProps: any;
  size: number;
  skipProps: any;
  isLastStep: boolean;
}

export const JoyRide = () => {
  const [run, setRun] = useState(false);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [isFirstRadioSelect, setIsFirstRadioSelect] = useState(true);
  const [isSecondRadioSelect, setIsSecondRadioSelect] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<any>([]);
  const dispatch: any = useAppDispatch();

  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const testtype =
    payload && payload.hasOwnProperty("testtype") && payload.testtype;
  const { isLoading, data, isError } = useGetOnboardingTourStatusQuery();
  const {
    isLoading: testScoreAndPerformanceLoading,
    isError: testScoreAndPerformanceError,
  } = useGetTestScoresAndPerformanceQuery();
  const {
    data: courseworkData,
    isLoading: courseWorkLoading,
    isError: courseWorkError,
  } = useGetCourseworkQuery({
    page: 1,
    ...(isDashboard
      ? {
          selectedOption: 1,
        }
      : {}),
    ...(isDashboard
      ? {
          timeFilter: 1,
        }
      : {}),
    ...(isSelfPaced && testtype === MCAT_TEST_TYPE
      ? {
          subject: 0, // INFO: 0 for initial "all subjects" filter
        }
      : {}),
  });

  const { isLoading: overallProgressLoading, isError: overallProgressError } =
    useGetOverallProgressQuery({
      Userid: userId,
      Productcode: productCode,
      Offeringcode: offeringCode,
    });

  // const isLoading = false;
  // const data = null;
  // const isError = false;

  const [PostTour, { isLoading: postTourIsLoading, isError: postTourIsError }] =
    useSaveOnboardingTourStatusMutation();

  const [
    themeBody,
    { isLoading: themeBodyIsLoading, isError: themeBodyIsError },
  ] = useSaveThemePreferenceMutation();

  const screen = useScreenSize();

  const handleThemeModalClose = async () => {
    let selectedTheme: "light" | "dark" = isFirstRadioSelect ? "light" : "dark";

    try {
      await themeBody({
        theme: selectedTheme,
      })
        .unwrap()
        .then(() => {
          setIsThemeModalOpen((prev) => !prev);
        });
    } catch (error: any) {
      console.log("error : ", error);
    }
  };

  const handleThemeModalDefaultClose = () => {
    setIsThemeModalOpen((prev) => !prev);
  };

  const handleRadioBtnClick = (type: string) => {
    if (type === "first") {
      if (isFirstRadioSelect) {
        setIsFirstRadioSelect((prev) => false);
        setIsSecondRadioSelect((prev) => true);
      } else {
        setIsFirstRadioSelect((prev) => true);
        setIsSecondRadioSelect((prev) => false);
      }
    } else {
      if (isSecondRadioSelect) {
        setIsFirstRadioSelect((prev) => true);
        setIsSecondRadioSelect((prev) => false);
      } else {
        setIsFirstRadioSelect((prev) => false);
        setIsSecondRadioSelect((prev) => true);
      }
    }
  };

  const Tooltip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    size,
    skipProps,
    isLastStep,
  }: toolTipProps) => {
    return (
      <div className="guided-tour-wrapper">
        <div className="body-wrapper">
          <Typography
            variant="h8Med"
            label={step.content}
            styles="guid-tour-text-custom-style"
          />
        </div>

        <div className="footer-wrapper">
          {/* <div className="left-btn">
            <Typography variant="h9Medium" label={`${index + 1}/${size}`} />
          </div> */}
          <div className="right-btn">
            <div className="skip-wrapper" onClick={skipProps.onClick}>
              <Typography
                variant="h9Medium"
                label={skipProps.title}
                styles="skip-text-custom-style"
              />
            </div>

            <Button
              label="Back"
              type="filter"
              icon={false}
              styles="back-button-custom-style"
              isLoading={postTourIsLoading}
              onClick={backProps.onClick}
              isDisabled={index === 0}
            />

            <Button
              label={isLastStep ? "Finish" : "Next"}
              type="appeal"
              icon={false}
              onClick={primaryProps.onClick}
              styles="button-custom-style"
              isLoading={postTourIsLoading}
            />
          </div>
        </div>
      </div>
    );
  };

  const staticSteps = [
    {
      content:
        "Welcome! 👋  Start your journey by clicking “Click here to start! View your prep info” on the Home page. Access your Student Guide, learn how to get started, and explore everything you need to succeed.",
      target: "#dashboard-view-prep-info-btn",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "On the Home page, you can view your course progress by clicking on the “Overall Progress Summary” card.",
      target: ".doughnut-card-section",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "Click “View Scores” to track your test scores and view details of your performance",
      target: ".horizontal-progress-bar-card-section",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "Click this button to view the course through the Study Planner.",
      target: ".tile-btn-style",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content: "Change the filters here to easily find specific coursework.",
      target: "#filter-btn-section",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "Filter Coursework by Recent and Future Sessions or Past Sessions",
      target: "#drop-down-section",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "View when the live classes will take place and add to your external calendar.",
      target: "#content-3-0",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content: "Click here to take online test",
      target: "#home-start-online-test-btn-0",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content: "Complete your diagnostic exam within this class segment tab",
      target: "#home-diagnostic",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content: "Complete your Prework within this class segment tab",
      target: "#home-prework",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content: "Complete your Class within this class segment tab",
      target: "#home-class",
      disableBeacon: true,
      disableOverlayClose: true,
    },
    {
      content:
        "Complete homework after class to test your knowledge and enforce your learning",
      target: "#home-homework",
      disableBeacon: true,
      disableOverlayClose: true,
    },
  ];

  const styles = {
    spotlight: {
      border: "5px solid #3272BE",
    },

    options: {
      overlayColor: "rgba(0, 0, 0, 0.25)",
    },
  };

  const handleRun = () => {
    if (screen.width > 574) {
      setRun((prev) => true);
    }
  };

  const joyRideCallBack = async ({
    index,
    size,
    status,
    type,
    action,
    step,
  }: CallBackProps) => {

    if (type === "error:target_not_found") {
      if (index + 1 === size) {
        // check if this last step
        setRun((prev) => false);
        setIsThemeModalOpen((prev) => true);
      } else {
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }
    }

    if (type === "step:before") {
      try {
        await PostTour({
          totalSteps: steps.length,
          currentStep: index,
          skipped: false,
        }).unwrap();
      } catch (error: any) {
        console.log("error : ", error);
      }
    }

    if (type === "step:after") {
      // setStepIndex((prev) => prev + 1);
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    if (ACTIONS.SKIP === action) {
      try {
        await PostTour({
          totalSteps: steps.length,
          currentStep: index,
          skipped: true,
        })
          .unwrap()
          .then(() => {
            setRun((prev) => false);
            setIsThemeModalOpen((prev) => true);
          });
      } catch (error: any) {
        console.log("error : ", error);
      }
    }

    let activeCarousel = document.querySelector(
      ".active-home-page-performance-carousel"
    );
    let item = document.getElementById("dot-1");
    let item2 = document.getElementById("dot-0");

    // when the user clicks on the next button after the step related to test-scores-and-performance carosal should move to test-scores-and-performance card

    if (
      index === 0 &&
      type === "step:after" &&
      ACTIONS.CLOSE === action &&
      activeCarousel &&
      !activeCarousel.querySelector("#test-scores-and-performance")
    ) {
      setRun(false);
      item?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }

    // when the user clicks on the back button after the step related to test-scores-and-performance carosal should move to doughnut-card-section card
    if (
      index === 2 &&
      type === "step:after" &&
      ACTIONS.PREV === action &&
      activeCarousel &&
      !activeCarousel.querySelector("#doughnut-card-section")
    ) {
      setRun(false);
      item2?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }

    // when the user refresh the browser and joiride in test-scores-and-performance card need to carosal move to it
    if (
      index === 2 &&
      type === "tour:start" &&
      activeCarousel &&
      !activeCarousel.querySelector("#test-scores-and-performance")
    ) {
      setRun(false);
      item?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }
    // when the user refresh the browser and joiride in filter section and click back button. carosal need to move to it
    if (
      index === 2 &&
      type === "step:before" &&
      ACTIONS.PREV === action &&
      activeCarousel &&
      !activeCarousel.querySelector("#test-scores-and-performance")
    ) {
      setRun(false);
      item?.click();
      const timer = setTimeout(() => {
        setRun(true);
        clearTimeout(timer);
      }, 200);

      return () => clearTimeout(timer);
    }

    if (status === STATUS.FINISHED) {
      try {
        await PostTour({
          totalSteps: steps.length,
          currentStep: steps.length,
          skipped: true,
        })
          .unwrap()
          .then(() => {
            setRun((prev) => false);
            setIsThemeModalOpen((prev) => true);
          });
      } catch (error: any) {
        console.log("error : ", error);
      }
    }
  };

  useEffect(() => {
    if (screen.width <= 574) {
      setRun((prev) => false);
    }
  }, [screen.width]);

  useEffect(() => {
    if (screen.width > 574) {
      if (
        (data === null || (data && !data.Skipped)) &&
        !isError &&
        !testScoreAndPerformanceLoading &&
        !courseWorkLoading &&
        !overallProgressLoading &&
        !testScoreAndPerformanceError &&
        !courseWorkError &&
        !overallProgressError
      ) {
        if (data && data.CurrentStep > 0) {
          setStepIndex((prev) => data.CurrentStep);
        }
        handleRun();
      }
    }
  }, [
    data,
    testScoreAndPerformanceLoading,
    courseWorkLoading,
    overallProgressLoading,
  ]);

  useEffect(() => {
    let copySteps: any = [...staticSteps];
    if (
      courseworkData &&
      courseworkData.Segments &&
      Array.isArray(courseworkData.Segments)
    ) {
      const hasToDoTestSegment = courseworkData.Segments.find(
        (segment: any, index: number) =>
          segment.IsTestSegment === true &&
          segment.ExamCo.Status === "Not Started" &&
          index === 0
      );

      if (hasToDoTestSegment !== undefined) {
        // setSteps(staticSteps);
        copySteps = [...staticSteps];
      } else {
        staticSteps.splice(6, 1);
        // setSteps(staticSteps);
        copySteps = [...staticSteps];
      }
    }
    // this if for remove step related to performance-summary when showperformancesummary is false
    // this is for remove step related to progress-summary when showoverallprogresssummary is false
    if (
      payload &&
      payload.hasOwnProperty("showoverallprogresssummary") &&
      payload.showoverallprogresssummary.toLowerCase() === "false"
    ) {
      copySteps.splice(1, 1);
    }
    // this is for remove step related to performance-summary when showperformancesummary is false
    if (
      payload &&
      payload.hasOwnProperty("showperformancesummary") &&
      payload.showperformancesummary.toLowerCase() === "false"
    ) {
      copySteps.splice(2, 1);
    }
    // this is for remove step related to study-planner when showstudyplanner is false
    if (
      payload &&
      payload.hasOwnProperty("showstudyplanner") &&
      payload.showstudyplanner.toLowerCase() === "false"
    ) {
      copySteps.splice(3, 1);
    }

    //this is for remove joyride for tabs in coswork if there is no relevent categories
    if (
      courseworkData &&
      courseworkData.Segments &&
      Array.isArray(courseworkData.Segments)
    ) {
      const hasCategories = courseworkData.Segments.find(
        (segment: any) => segment.Categories !== null
      );

      if (hasCategories) {
        const hasDiagnosticCategories = courseworkData.Segments.some(
          (segment: any) => {
            if (!segment.Categories) return false;
            return Object.keys(segment.Categories).some((group: string) => {
              const normalized = group.toLowerCase();
              return (
                normalized === "diagnostic exam" ||
                normalized === "diagnostic exams" ||
                normalized === "diagnostic exam a" ||
                normalized === "diagnostic exam b"
              );
            });
          }
        );

        const hasHomeworkCategories = courseworkData.Segments.some(
          (segment: any) => {
            if (!segment.Categories) return false;
            return Object.keys(segment.Categories).some(
              (group: string) => group.toLowerCase() === "homework"
            );
          }
        );

        const hasClassCategories = courseworkData.Segments.some(
          (segment: any) => {
            if (!segment.Categories) return false;
            return Object.keys(segment.Categories).some(
              (group: string) => group.toLowerCase() === "class"
            );
          }
        );

        const hasPreworkCategories = courseworkData.Segments.some(
          (segment: any) => {
            if (!segment.Categories) return false;
            return Object.keys(segment.Categories).some(
              (group: string) => group.toLowerCase() === "prework"
            );
          }
        );

        // Indices of the final 4 steps to potentially remove
        const removalIndexes = [];

        if (!hasHomeworkCategories) removalIndexes.push(copySteps.length - 1);
        if (!hasClassCategories) removalIndexes.push(copySteps.length - 2);
        if (!hasPreworkCategories) removalIndexes.push(copySteps.length - 3);
        if (!hasDiagnosticCategories) removalIndexes.push(copySteps.length - 4);

        // Remove in reverse order to avoid index shift
        removalIndexes
          .sort((a, b) => b - a)
          .forEach((index) => copySteps.splice(index, 1));
      } else {
        // Remove final 4 if no categories exist

        if (courseworkData.Segments.length === 0) {
          copySteps.splice(-5);
        } else {
          copySteps.splice(-4);
        }
      }
    }
    setSteps([...copySteps]);
  }, [courseworkData]);

  return (
    <>
      {!isLoading && !isError && !courseWorkLoading && (
        <Joyride
          tooltipComponent={Tooltip}
          run={run}
          steps={steps}
          styles={styles}
          callback={joyRideCallBack}
          stepIndex={stepIndex}
        />
      )}
      {isThemeModalOpen && (
        <Modal
          closeOnOutsideClick={false}
          isOpen={isThemeModalOpen}
          onClose={handleThemeModalDefaultClose}
          theme="light"
        >
          <div className="theme-modal-wrapper">
            <div className="theme-modal-header">
              <Typography
                variant="h8Med"
                label="Choose your theme"
                styles="header-custom-style"
              />
            </div>
            <div className="theme-modal-body">
              <div className="content-section">
                <div className="radio-btn-section">
                  <div className="radio">
                    <RadioButton
                      isSelected={isFirstRadioSelect}
                      onClick={handleRadioBtnClick}
                      type="first"
                    />
                    <div className="mode">
                      <Typography variant="h8Med" label="Light Mode" />
                    </div>
                  </div>
                  <div className="radio">
                    <RadioButton
                      isSelected={isSecondRadioSelect}
                      onClick={handleRadioBtnClick}
                      type="second"
                    />
                    <div className="mode">
                      <Typography variant="h8Med" label="Dark Mode" />
                    </div>
                  </div>
                </div>
                <div className="typography-section">
                  <Typography
                    variant="h8Book"
                    label="You can change your theme anytime within My Account > Settings"
                  />
                </div>
              </div>
              <div className="btn-section">
                <Button
                  label="Save"
                  icon={false}
                  type="score"
                  onClick={handleThemeModalClose}
                  isLoading={themeBodyIsLoading}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
